// Facts → the `players` serving table.
//
// `players` is a PROJECTION. Nothing is authored here and nothing is lost if
// the whole table is dropped: it is rebuilt from `fact`, and that property is
// the test of whether the line between layer 3 and the app is drawn correctly.
// What it buys is a directory query that is one indexed table scan instead of
// four joins over an append-only history, on the screen people open most.
//
// It ADDS AND UPDATES, never deletes. ICD-P01 invariant 3 — nobody is deleted;
// a person who leaves every roster keeps their id and their record and goes
// `inactive`. The one case where that leaves a stale row is a person who stops
// qualifying for the directory at all, which today only happens if the position
// mapping changes underneath them. Clearing those is a deliberate act against a
// specific set of ids, not something a nightly run should ever do on its own.
//
// It is a FULL rebuild, every run, on purpose: every row is computed from the
// facts each time, so there is no delta bookkeeping that could drift from them.
// But a row is WRITTEN only when it changed (`changedOnly`, DATA_LAYER_DESIGN
// Phase 0) — every rebuild used to rewrite all ~6,000, 258,030 rows written in
// production's week to 2026-10-05, to move a few designations. ICD-P01 defines
// `asOf` as "when a source last confirmed this record", and a daily file that
// lists somebody IS a source confirming them; that instant is the rebuild's,
// kept in `directory_builds` (0037), which `/api/players` reads beside the
// row's own `updated_at` (now when it last changed), so the wire keeps its
// meaning. And since 2026-10-22 the caller sends a statement only for a row
// that differs from the stored one (`rows`, `storedAs`), and reads vendor keys
// only for the people who need them (PLAN-2026-10-10 §4c): every row is still
// computed, from every person's facts.
//
// THE SPORT LIVES IN THE MAPPING, NOT HERE. This file never compares a position
// to a literal, never knows that QB is a fantasy position and DE is not, and
// never sees a sport id except as a string to store. `DirectoryMapping` is
// supplied by the sport package at the composition root. That is the difference
// between a projection that works for basketball later and one that has to be
// written twice.
//
// Pure: rows in, statements out, a clock passed as an argument.

/**
 * The upsert's condition: write the row only when a column it sets differs.
 * `IS NOT` so that a null against a value counts and two nulls do not.
 */
const changedOnly = (columns: readonly string[]): string =>
  `WHERE ${columns.map((c) => `players.${c} IS NOT excluded.${c}`).join(' OR ')}`;

/** What a club's upsert sets, `updated_at` aside. */
const CLUB_COLUMNS = [
  'sport',
  'kind',
  'first_name',
  'last_name',
  'display_name',
  'short_name',
  'positions_json',
  'pro_team',
  'status',
  'external_ids_json',
] as const;

/**
 * How long a short name may be (ICD-P01 §6.1).
 *
 * A short name exists for the places a full one will not fit — a lineup row, a
 * chip, a phone-width table — so it has a hard cap rather than a suggestion.
 * The longest real one nflverse carries is 22 characters, which is why nothing
 * enforced this for a year: no producer had ever been handed a name long enough
 * to break it. The simulator's stress names are, deliberately.
 *
 * HERE, BELOW THE WIRE, since 2026-10-08: the projection that writes the row
 * holds to it, and `@fsp/domain` takes the number from here. It lived only on
 * the wire's shape, so the projection wrote the simulator's 25-character names
 * into `players` and `/api/players` refused every page that held one — found
 * when the directory artifact (ICD-S10) refused to build from the same rows.
 */
export const SHORT_NAME_MAX = 24;

/**
 * A short name that fits the cap: as it is when it does, else cut to it with
 * an ellipsis saying there was more — the simulator's provider's rule for the
 * same problem, which keeps the initial and as much family name as fits.
 */
export function fitShortName(name: string | null): string | null {
  if (name === null || name.length <= SHORT_NAME_MAX) return name;
  return `${name.slice(0, SHORT_NAME_MAX - 1).trimEnd()}…`;
}

/** What a person's upsert sets, `updated_at` aside. */
export const PERSON_COLUMNS = [
  ...CLUB_COLUMNS,
  'injury_status',
  'injury_note',
  'injury_read_at',
  'practice_status',
  'experience_years',
  'depth_position',
  'depth_rank',
  'pro_draft_pick',
] as const;

export type PersonColumn = (typeof PERSON_COLUMNS)[number];

/** A person's row as the projection computes it: every column the upsert compares. */
export interface DirectoryRow {
  id: string;
  sport: string;
  kind: 'person';
  first_name: string;
  last_name: string;
  display_name: string;
  short_name: string | null;
  positions_json: string;
  pro_team: string | null;
  status: DirectoryStatus;
  injury_status: string | null;
  injury_note: string | null;
  /**
   * When the report his designation is from was first read as it stands
   * (0099); null for a designation his club's roster gives, and with none.
   */
  injury_read_at: string | null;
  practice_status: string | null;
  experience_years: number;
  /** The column of his club's depth chart he is listed in, in the chart's own word (0083). */
  depth_position: string | null;
  /** His rank there, 1 the first string; null with `depth_position`. */
  depth_rank: number | null;
  /** The overall pick the pro league drafted him with, 1 the first; null undrafted (0087). */
  pro_draft_pick: number | null;
  external_ids_json: string;
}

/**
 * Whether a stored row already says what the projection computed, by the
 * upsert's own test (`changedOnly`): every column it compares equal, and a
 * null equal only to a null. `except` leaves columns out of the test — the
 * vendor keys, for a row computed before they were read.
 *
 * So a rebuild sends a statement only for a row that differs, where it sent
 * one for every person and the upsert's guard found most of them unchanged
 * (PLAN-2026-10-10 §4c).
 */
export function storedAs(
  row: DirectoryRow,
  stored: Readonly<Record<string, unknown>> | undefined,
  except: readonly PersonColumn[] = [],
): boolean {
  if (stored === undefined) return false;
  return PERSON_COLUMNS.every((c) => except.includes(c) || row[c] === (stored[c] ?? null));
}

import { z } from 'zod';
import { DepthPosition, InjuryReport, PersonProfile, RosterMembership } from '../kinds/registry';
import type { Statement } from '../store/statements';

export type DirectoryStatus = 'active' | 'inactive' | 'practice-squad' | 'retired';

/** How much a player practiced on his latest report: in full, limited, or not at all. */
export type PracticeLevel = 'full' | 'limited' | 'none';

/** Everything sport-specific about turning facts into a directory row. */
export interface DirectoryMapping {
  /** Stored on every row and used by every query. */
  sport: string;
  /**
   * The source's position words → our position ids, most significant first.
   * Empty is a legitimate answer: an offensive tackle fills no fantasy slot,
   * and inventing one for them would put them in the draft room.
   */
  positions(position: string | null, depthPosition: string | null): readonly string[];
  /** Whether they can appear in a game. Not an injury — see ICD-P01 §6.1. */
  status(input: {
    rosterStatus: string | null;
    profileStatus: string | null;
    onCurrentRoster: boolean;
  }): DirectoryStatus;
  /** A game-day designation → an `InjuryStatusDef` id, or null for none. */
  injuryStatus(reportStatus: string | null): string | null;
  /**
   * The designation of somebody his club holds on a reserve list this week —
   * injured reserve, PUP — from the roster's own code, as an `InjuryStatusDef`
   * id, or null when the roster says nothing of the kind.
   *
   * The weekly report lists who might miss a game, and a player on injured
   * reserve is not on it, so he had no designation at all: no IR on his card,
   * and a league's IR slot, which takes a player by his designation, would not
   * take him. Optional: a sport whose source has no finer word leaves its
   * reserve players with the report's.
   */
  reserveStatus?(roster: { status: string | null; detail: string | null }): string | null;
  /**
   * The source's practice wording → something a person would read.
   *
   * Separate from the designation because they answer different questions, and
   * for two thirds of the report the practice column is the only thing there
   * is to say.
   */
  practiceStatus(practiceStatus: string | null): string | null;
  /**
   * How much a player practiced, from our own practice wording
   * (`practiceStatus`'s answer, as `players.practice_status` holds it): what a
   * bot weighs a Questionable player by (B-503). Optional, and null for a
   * wording that says neither: a sport whose report has no practice column
   * leaves its bots to the designation alone.
   */
  practiceLevel?(practice: string): PracticeLevel | null;
  /**
   * A short name for somebody the source gave none, in this sport's own
   * convention — football's is "B.Purdy".
   *
   * Asked here, in the projection, so the row carries it and every client
   * reads the same one: 3,502 of 6,081 football rows had none on 2026-09-25,
   * and a lineup read "B.Purdy" on one row and "Quinshon Judkins" on the next.
   * Asked OF THE SPORT because ICD-P01 §6.1 says why the column is stored
   * rather than derived: "first initial, last name" is a football convention,
   * not a universal one. Optional, and null is a real answer — a sport with no
   * convention leaves the column empty and a client shows the display name.
   * The source's own short name always wins; this is only ever the fallback.
   */
  shortName?(name: { display: string; first: string; last: string }): string | null;
  /**
   * A club as a roster-able unit — football's D/ST — or null for a club this
   * sport does not draft as a whole, or a sport with no such position.
   *
   * `keys` are every source key the `team` entity carries plus its display,
   * in the source's spelling; the sport decides which club they name and what
   * to call it. Optional, because a sport without a team unit has nothing to
   * say here and an empty answer should not have to be written.
   */
  club?(keys: readonly string[]): DirectoryClubUnit | null;
  /**
   * Pages on other sites that keep a player's game-day status current, from
   * his vendor keys (`externalIds`): each the site's name and the page's
   * address, which a client offers as a plain link out, never framed or
   * copied (B-482). Our own news is the league office's injury report, and
   * it lags game time (DIRECTION D10). Asked of the sport because an address
   * is a vendor's convention for that sport's league. Optional: a sport with
   * none links nowhere, and a player with no key there gets none.
   */
  playerPages?(externalIds: Readonly<Record<string, string>>): readonly DirectoryPlayerPage[];
}

/** A page on another site about one player (`DirectoryMapping.playerPages`). */
export interface DirectoryPlayerPage {
  /** The site, as a person names it: "ESPN". */
  site: string;
  /** The page's address, https. */
  url: string;
}

/** What a sport says a club is, when a roster can hold the whole club. */
export interface DirectoryClubUnit {
  /** The position id the club's row fills: football's `DST`. */
  position: string;
  /** The source's own code for the club, as `players.pro_team` spells it for people. */
  code: string;
  /** 'Kansas City Chiefs' */
  name: string;
  /** 'Chiefs' */
  shortName: string;
  /** 'Kansas City' */
  location: string;
}

/** A `team` entity, as the projection is handed it. */
export interface DirectoryClub {
  entityId: string;
  display: string;
  /** namespace → key, every source id we hold for the club. */
  externalIds: Record<string, string>;
}

/** One person, with the current facts about them, exactly as stored. */
export interface DirectoryPerson {
  entityId: string;
  display: string;
  /** namespace → key, every vendor id we have ever learned. ICD-P01 §6.1. */
  externalIds: Record<string, string>;
  /** `payload_json` of the current `person.profile`, if there is one. */
  profile: unknown;
  /** The newest current `roster.membership`. */
  membership: unknown;
  /** The newest current `injury.report`. */
  report: unknown;
  /**
   * When that report was first read as it stands: its fact's `observed_at`,
   * which the reconcile leaves alone while the row is unchanged. The source
   * carries no filing date, so this is the date a designation is shown with
   * (B-52). Absent or null: not known, and the row says none.
   */
  reportReadAt?: string | null;
  /**
   * Every current `depth.position` of his, the chart's own snapshot: one a
   * column he is listed in (a receiver who also returns punts has two).
   */
  depth: readonly unknown[];
}

export interface ProjectionResult {
  statements: Statement[];
  /**
   * The people's rows the statements write, in statement order, for a caller
   * to compare with what is stored (`storedAs`). Clubs' rows are not here.
   */
  rows: DirectoryRow[];
  /** The period the directory is speaking about, for the run ledger. */
  season: number | null;
  period: number | null;
  written: number;
  /**
   * The entity id of every person a row was computed for, in statement order.
   * Who is kept is decided by the profile, the membership and the position,
   * never by a vendor key, so a caller can project once without keys, read
   * the keys of just these people, and project again (DATA_LAYER_DESIGN §5,
   * Phase 2: the key read was 367,003 rows for every person ever seen).
   */
  people: string[];
  /** How many of `written` are clubs rather than people. */
  clubs: number;
  /** People the projection refused to write, with the reason. Never silent. */
  skipped: { reason: string; count: number }[];
}

/**
 * A `players` row per club, so a roster can hold one.
 *
 * THIS IS HOW A TEAM DEFENSE BECOMES DRAFTABLE, and it is a projection rather
 * than an identity: the club IS the `team` entity the schedules assert, the row
 * takes the entity's own id exactly as a person's row does, and no
 * pseudo-person is minted anywhere (the Info Manager plan refused that on
 * 2026-09-12). Downstream, `roster_players`, `lineup_slots` and
 * `player_period_stats` reference `players.id` and never learn the difference.
 *
 * A club the sport does not recognize — a relocated franchise's old code from
 * a 1999 schedule, say — is skipped and counted, never invented.
 */
export function projectClubs(
  clubs: readonly DirectoryClub[],
  mapping: DirectoryMapping,
  now: string,
): { statements: Statement[]; written: number; skipped: { reason: string; count: number }[] } {
  const statements: Statement[] = [];
  let skipped = 0;
  if (mapping.club === undefined) return { statements, written: 0, skipped: [] };

  for (const club of clubs) {
    const unit = mapping.club([...Object.values(club.externalIds), club.display]);
    if (unit === null) {
      skipped++;
      continue;
    }
    statements.push({
      sql: `INSERT INTO players
              (id, sport, kind, first_name, last_name, display_name, short_name, positions_json,
               pro_team, status, injury_status, injury_note, practice_status,
               experience_years, external_ids_json, updated_at)
            VALUES (?, ?, 'club', ?, ?, ?, ?, ?, ?, 'active', NULL, NULL, NULL, 0, ?, ?)
            ON CONFLICT(id) DO UPDATE SET
              sport = excluded.sport,
              kind = excluded.kind,
              first_name = excluded.first_name,
              last_name = excluded.last_name,
              display_name = excluded.display_name,
              short_name = excluded.short_name,
              positions_json = excluded.positions_json,
              pro_team = excluded.pro_team,
              status = excluded.status,
              external_ids_json = excluded.external_ids_json,
              updated_at = excluded.updated_at
            ${changedOnly(CLUB_COLUMNS)}`,
      params: [
        club.entityId,
        mapping.sport,
        unit.location,
        unit.shortName,
        unit.name,
        unit.shortName,
        JSON.stringify([unit.position]),
        unit.code,
        JSON.stringify(club.externalIds),
        now,
      ],
    });
  }

  return {
    statements,
    written: statements.length,
    skipped: skipped > 0 ? [{ reason: 'no club this sport rosters', count: skipped }] : [],
  };
}

/** A membership or report, as far as the projection needs to order them. */
const Weekly = z.object({ season: z.number().int(), period: z.number().int() });

function parse<T>(
  schema: { safeParse(v: unknown): { success: true; data: T } | { success: false } },
  value: unknown,
): T | null {
  const out = schema.safeParse(value);
  return out.success ? out.data : null;
}

/**
 * Split a display name when the source did not give the parts.
 *
 * `first_name` and `last_name` are NOT NULL and they are the sort key, so there
 * has to be an answer. The last whitespace-separated token is the family name
 * often enough to be useful and is never wrong in a way that loses the person —
 * `display_name` is what a card actually shows.
 */
function splitName(display: string): { first: string; last: string } {
  const parts = display.trim().split(/\s+/);
  if (parts.length === 1) return { first: parts[0]!, last: parts[0]! };
  return { first: parts[0]!, last: parts.slice(1).join(' ') };
}

export function projectDirectory(
  people: readonly DirectoryPerson[],
  mapping: DirectoryMapping,
  now: string,
): ProjectionResult {
  // Which week the directory is speaking about. Taken from the newest
  // membership anywhere, never from the clock: the data's own idea of "now" is
  // the only one that stays right when a run is replayed or a file lags.
  let season: number | null = null;
  let period: number | null = null;
  for (const p of people) {
    const w = parse(Weekly, p.membership);
    if (w === null) continue;
    if (season === null || w.season > season) {
      season = w.season;
      period = w.period;
    } else if (w.season === season && w.period > (period ?? 0)) {
      period = w.period;
    }
  }

  // EACH CLUB'S NEWEST WEEK THIS SEASON. A week's roster file leaves out the
  // clubs on their bye — 2026's week 6 had 28 of 32, without CIN, DET, MIA
  // and MIN — and reading only the newest week made every player of a club
  // on its bye inactive with no club for that week: gone from every league's
  // pool, and "Not on the active roster" in his manager's lineup (the Players
  // review of 2026-10-17). So a club missing from the newest week keeps the
  // roster it filed the week before. One week only: a bye is one week, and a
  // club silent for longer has nothing current to say.
  const clubWeek = new Map<string, number>();
  for (const p of people) {
    const m = parse(RosterMembership, p.membership);
    if (m === null || m.season !== season) continue;
    clubWeek.set(m.team, Math.max(clubWeek.get(m.team) ?? 0, m.period));
  }

  const statements: Statement[] = [];
  const rows: DirectoryRow[] = [];
  const kept: string[] = [];
  const skipped = new Map<string, number>();
  const skip = (reason: string) => skipped.set(reason, (skipped.get(reason) ?? 0) + 1);

  for (const person of people) {
    const profile = parse(PersonProfile, person.profile);
    const membership = parse(RosterMembership, person.membership);
    const report = parse(InjuryReport, person.report);

    if (profile === null && membership === null) {
      // Nothing to render. A person asserted by a fact that carries no name or
      // team — a `mentions` edge on a news story, later — is a real entity and
      // is simply not a directory row.
      skip('no profile and no membership');
      continue;
    }

    const display = profile?.displayName ?? person.display;
    const fallback = splitName(display);
    const first = profile?.firstName ?? fallback.first;
    const last = profile?.lastName ?? fallback.last;

    // Only the CURRENT week counts. Week 1's roster answered in December is
    // wrong in the way nobody notices: a real row, from a real file, that
    // stopped being true in September.
    const onCurrentRoster =
      membership !== null &&
      membership.season === season &&
      period !== null &&
      (membership.period === period ||
        (membership.period === period - 1 && clubWeek.get(membership.team) === membership.period));

    // Same rule for the designation, and here it is load-bearing. A report is
    // about one week; carrying last week's Questionable into this one is the
    // failure ICD-P01 §6.3 names.
    const current =
      report !== null && report.season === season && report.period === period ? report : null;

    const positions = mapping.positions(
      membership?.position ?? profile?.position ?? null,
      membership?.depthPosition ?? null,
    );

    // Somebody this sport has no position for. A left tackle is a real person
    // with a real entity and a full history in `fact`; they are simply not a
    // row in a fantasy directory, and `DirectoryPlayer.positions` requires at
    // least one (ICD-P01 §6.1). Measured on the first real run: 10,774 of
    // 16,619. Counted rather than dropped quietly, because a jump in this
    // number is what a new position vocabulary looks like from here.
    //
    // The alternative — keeping them with an empty position list — is worth
    // revisiting when the news layer (P-05) lands, since a story can mention
    // anybody and would then point at an entity with no directory row.
    if (positions.length === 0) {
      skip('no position this sport drafts');
      continue;
    }

    const status = mapping.status({
      rosterStatus: membership?.status ?? null,
      profileStatus: profile?.status ?? null,
      onCurrentRoster,
    });

    kept.push(person.entityId);
    // ON A RESERVE LIST, THE ROSTER'S WORD WINS: it is this week's, and it
    // outlasts any one game's designation.
    const reserve =
      onCurrentRoster && membership !== null
        ? (mapping.reserveStatus?.({
            status: membership.status,
            detail: membership.statusDetail,
          }) ?? null)
        : null;
    // OFF THE ACTIVE ROSTER, ONLY A RESERVE LIST DESIGNATES HIM. A game-day
    // word for somebody released is about a game he is no longer in, and an
    // Out on him read as a list a league's IR takes, which put him in every
    // league's pool (the review of 2026-10-17).
    const injuryStatus =
      reserve ??
      (current === null || status === 'inactive'
        ? null
        : mapping.injuryStatus(current.reportStatus));
    // DATED WHEN THE REPORT GAVE IT (B-52): a reserve list's is the roster's
    // word this week, which no report files.
    const injuryReadAt =
      reserve === null && injuryStatus !== null ? (person.reportReadAt ?? null) : null;
    const practiceStatus = current === null ? null : mapping.practiceStatus(current.practiceStatus);

    const row: DirectoryRow = {
      id: person.entityId,
      sport: mapping.sport,
      kind: 'person',
      first_name: first,
      last_name: last,
      display_name: display,
      short_name: fitShortName(
        profile?.shortName ?? mapping.shortName?.({ display, first, last }) ?? null,
      ),
      positions_json: JSON.stringify(positions),
      // Null is "not on a pro roster", never "we could not tell" — ICD-P01
      // invariant 9. Not knowing is a failed run, not a null.
      pro_team: onCurrentRoster ? (membership?.team ?? null) : null,
      status,
      injury_status: injuryStatus,
      // The source's plain-text detail, and the practice signal with it. The
      // practice column is the half Sleeper had no concept of, so losing it
      // on the way to the screen would waste the reason we changed source.
      injury_note: current === null ? null : injuryNote(current, practiceStatus),
      injury_read_at: injuryReadAt,
      practice_status: practiceStatus,
      experience_years: membership?.yearsExperience ?? profile?.yearsExperience ?? 0,
      ...depthOf(
        person.depth,
        positions[0]!,
        onCurrentRoster ? (membership?.team ?? null) : null,
        mapping,
      ),
      // WHERE THE PRO DRAFT TOOK HIM, which the board ranks a player it has no
      // season for by (0087): the profile's overall pick, null undrafted.
      pro_draft_pick:
        profile?.draftPick !== null && profile?.draftPick !== undefined && profile.draftPick > 0
          ? Math.trunc(profile.draftPick)
          : null,
      external_ids_json: JSON.stringify(person.externalIds),
    };
    rows.push(row);
    statements.push(personStatement(row, now));
  }

  return {
    statements,
    rows,
    season,
    period,
    written: statements.length,
    people: kept,
    clubs: 0,
    skipped: [...skipped.entries()].map(([reason, count]) => ({ reason, count })),
  };
}

/** A person's upsert: the row, written only when a column it sets differs. */
export function personStatement(row: DirectoryRow, now: string): Statement {
  return {
    sql: `INSERT INTO players
            (id, sport, kind, first_name, last_name, display_name, short_name, positions_json,
             pro_team, status, injury_status, injury_note, injury_read_at, practice_status,
             experience_years, depth_position, depth_rank, pro_draft_pick, external_ids_json,
             updated_at)
          VALUES (?, ?, 'person', ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
          ON CONFLICT(id) DO UPDATE SET
            sport = excluded.sport,
            kind = excluded.kind,
            first_name = excluded.first_name,
            last_name = excluded.last_name,
            display_name = excluded.display_name,
            short_name = excluded.short_name,
            positions_json = excluded.positions_json,
            pro_team = excluded.pro_team,
            status = excluded.status,
            injury_status = excluded.injury_status,
            injury_note = excluded.injury_note,
            injury_read_at = excluded.injury_read_at,
            practice_status = excluded.practice_status,
            experience_years = excluded.experience_years,
            depth_position = excluded.depth_position,
            depth_rank = excluded.depth_rank,
            pro_draft_pick = excluded.pro_draft_pick,
            external_ids_json = excluded.external_ids_json,
            updated_at = excluded.updated_at
          ${changedOnly(PERSON_COLUMNS)}`,
    params: [
      row.id,
      row.sport,
      row.first_name,
      row.last_name,
      row.display_name,
      row.short_name,
      row.positions_json,
      row.pro_team,
      row.status,
      row.injury_status,
      row.injury_note,
      row.injury_read_at,
      row.practice_status,
      row.experience_years,
      row.depth_position,
      row.depth_rank,
      row.pro_draft_pick,
      row.external_ids_json,
      now,
    ],
  };
}

/**
 * His place on his club's depth chart: the column he ranks highest in among
 * those that map to his first position in the sport's words, on the club he
 * is rostered by (a chart of a club he has left is not his place), and his
 * rank there. The column is the CHART'S OWN WORD — a fullback's `FB`, a place
 * kicker's `PK`, a corner's `RCB` — because the sport folds several columns
 * into one position: a starting fullback is not a starting running back, and
 * twelve clubs list one first in each (the projection's own finding,
 * 2026-09-25). Columns are mapped as a roster's depth position is
 * (`mapping.positions`), so a kick returner's maps to nothing. At one rank in
 * two columns, the column named as his position wins, then the first by name.
 * Null on no current chart.
 */
export function depthOf(
  facts: readonly unknown[],
  position: string,
  team: string | null,
  mapping: Pick<DirectoryMapping, 'positions'>,
): { depth_position: string | null; depth_rank: number | null } {
  let best: { column: string; rank: number } | null = null;
  const better = (column: string, rank: number): boolean => {
    if (best === null || rank !== best.rank) return best === null || rank < best.rank;
    const own = (c: string) => (c === position ? 0 : 1);
    return (
      own(column) - own(best.column) < 0 ||
      (own(column) === own(best.column) && column < best.column)
    );
  };
  if (team !== null) {
    for (const raw of facts) {
      const d = parse(DepthPosition, raw);
      if (d === null || d.team !== team || d.position === null || d.rank < 1) continue;
      if (!mapping.positions(null, d.position).includes(position)) continue;
      if (better(d.position, d.rank)) best = { column: d.position, rank: d.rank };
    }
  }
  return best === null
    ? { depth_position: null, depth_rank: null }
    : { depth_position: best.column, depth_rank: best.rank };
}

/**
 * The note a player card shows under the designation.
 *
 * Both halves, because they answer different questions: what they are listed
 * with, and whether they practised. "Ankle — did not practise" and "Ankle —
 * full practice" are the same designation and not remotely the same news.
 */
function injuryNote(report: z.infer<typeof InjuryReport>, practice: string | null): string | null {
  const what = report.reportInjury ?? report.practiceInjury;
  if (what === null && practice === null) return null;
  if (practice === null) return what;
  if (what === null) return practice;
  return `${what} — ${practice}`;
}
